import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  AIAutoSendPausedWebhookPayloadSchema,
  AINotesRegeneratedWebhookPayloadSchema,
  AITimesheetGeneratedWebhookPayloadSchema,
  CreateTimesheetProposalEntryOnlySchema,
  CreateTimesheetProposalEntryResponseSchema,
  CreateTimesheetProposalResponseSchema,
  CreateTimesheetProposalSchema,
  DeleteTimesheetProposalEntryResponseSchema,
  ErrorGenericSchema,
  GenericUUID,
  GetTimesheetProposalResponseSchema,
  GetTimesheetProposalsQuerySchema,
  GetTimesheetProposalsResponseSchema,
  OKGeneric,
  PatchTimesheetProposalEntrySchema,
  RegenerateTimesheetProposalNotesResponseSchema,
  RegenerateTimesheetProposalNotesSchema,
  TimesheetProposalConflictErrorSchema,
  TimesheetProposalNotFoundErrorSchema,
  TimesheetProposalValidationErrorSchema,
  TriggerTimesheetProposalGenerationResponseSchema,
  TriggerTimesheetProposalGenerationSchema,
  UpdateTimesheetProposalStatusSchema,
  URLParamsSchema,
} from '../models';

extendZodWithOpenApi(z);

const c = initContract();

/**
 * TIMESHEET PROPOSALS API CONTRACT
 *
 * This contract defines the API endpoints for the timesheet proposals system,
 * which allows AI to generate timesheet suggestions based on user activity and memories.
 */
export const apiTimesheetProposals = c.router({
  // ================================================
  // PROPOSAL MANAGEMENT
  // ================================================

  /**
   * Get a list of timesheet proposals
   */
  getTimesheetProposals: {
    method: 'GET',
    path: `/1.0/timesheet-proposals`,
    description:
      'Get a paginated list of timesheet proposals for the authenticated user, with optional filtering by connection, status, and date range.',
    summary: 'List timesheet proposals',
    query: GetTimesheetProposalsQuerySchema,
    responses: {
      200: GetTimesheetProposalsResponseSchema,
      400: TimesheetProposalValidationErrorSchema,
      500: ErrorGenericSchema,
    },
  },

  /**
   * Get a specific timesheet proposal with full details
   */
  getTimesheetProposal: {
    method: 'GET',
    path: `/1.0/timesheet-proposals/:id`,
    description:
      'Get a complete timesheet proposal including all entries and time blocks. This provides the full details needed for review and editing.',
    summary: 'Get timesheet proposal details',
    pathParams: z.object({
      id: GenericUUID.openapi({
        description: 'ID of the timesheet proposal to retrieve',
      }),
    }),
    responses: {
      200: GetTimesheetProposalResponseSchema,
      404: TimesheetProposalNotFoundErrorSchema,
      500: ErrorGenericSchema,
    },
  },

  /**
   * Create a new timesheet proposal (typically AI-generated)
   */
  createTimesheetProposal: {
    method: 'POST',
    path: `/1.0/timesheet-proposals`,
    description:
      'Create a new timesheet proposal with entries and time blocks. This is typically called by the AI system after generating suggestions, but can also be used to create manual proposals.',
    summary: 'Create timesheet proposal',
    body: CreateTimesheetProposalSchema,
    responses: {
      201: CreateTimesheetProposalResponseSchema,
      400: TimesheetProposalValidationErrorSchema,
      409: TimesheetProposalConflictErrorSchema,
      500: ErrorGenericSchema,
    },
  },

  /**
   * Update the status of a timesheet proposal
   */
  updateTimesheetProposalStatus: {
    method: 'PATCH',
    path: `/1.0/timesheet-proposals/:id/status`,
    description:
      'Update the status of a timesheet proposal (accept, reject, mark as edited). This operation is logged in the proposal audit trail.',
    summary: 'Update proposal status',
    pathParams: z.object({
      id: GenericUUID.openapi({
        description: 'ID of the timesheet proposal to update',
      }),
    }),
    body: UpdateTimesheetProposalStatusSchema,
    responses: {
      200: GetTimesheetProposalResponseSchema,
      400: TimesheetProposalValidationErrorSchema,
      404: TimesheetProposalNotFoundErrorSchema,
      500: ErrorGenericSchema,
    },
  },

  /**
   * Delete a timesheet proposal (soft delete)
   */
  deleteTimesheetProposal: {
    method: 'DELETE',
    path: `/1.0/timesheet-proposals/:id`,
    description:
      'Soft delete a timesheet proposal. The proposal will be marked as deleted but retained for audit purposes.',
    summary: 'Delete timesheet proposal',
    pathParams: z.object({
      id: GenericUUID.openapi({
        description: 'ID of the timesheet proposal to delete',
      }),
    }),
    responses: {
      200: OKGeneric,
      404: TimesheetProposalNotFoundErrorSchema,
      500: ErrorGenericSchema,
    },
  },

  // ================================================
  // ENTRY MANAGEMENT
  // ================================================

  /**
   * Create a new entry within an existing proposal
   */
  createTimesheetProposalEntry: {
    method: 'POST',
    path: `/1.0/timesheet-proposals/:proposalId/entries`,
    description:
      'Create a new timesheet proposal entry with optional time blocks within an existing proposal. This allows users to add additional work entries to a proposal.',
    summary: 'Create new entry',
    pathParams: z.object({
      proposalId: GenericUUID.openapi({
        description: 'ID of the timesheet proposal to add the entry to',
      }),
    }),
    body: CreateTimesheetProposalEntryOnlySchema,
    responses: {
      201: CreateTimesheetProposalEntryResponseSchema,
      400: TimesheetProposalValidationErrorSchema,
      404: TimesheetProposalNotFoundErrorSchema,
      500: ErrorGenericSchema,
    },
  },

  /**
   * Delete a specific entry and move its time blocks to unassigned
   */
  deleteTimesheetProposalEntry: {
    method: 'DELETE',
    path: `/1.0/timesheet-proposals/entries/:entryId`,
    description:
      'Delete a timesheet proposal entry, moving any associated time blocks to an unassigned entry (null project_id) to preserve time tracking data. This operation is logged in the parent proposal audit trail.',
    summary: 'Delete entry, preserve time blocks',
    pathParams: z.object({
      entryId: GenericUUID.openapi({
        description: 'ID of the proposal entry to delete',
      }),
    }),
    responses: {
      200: DeleteTimesheetProposalEntryResponseSchema,
      404: TimesheetProposalNotFoundErrorSchema,
      500: ErrorGenericSchema,
    },
  },

  /**
   * Patch fields for a specific entry
   */
  patchTimesheetProposalEntry: {
    method: 'PATCH',
    path: `/1.0/timesheet-proposals/entries/:entryId`,
    description:
      'Patch fields for a specific proposal entry. This allows users to update fields like note, hour_id, or duration_minutes without replacing the entire entry.',
    summary: 'Patch entry fields',
    pathParams: z.object({
      entryId: GenericUUID.openapi({
        description: 'ID of the proposal entry to update',
      }),
    }),
    body: PatchTimesheetProposalEntrySchema,
    responses: {
      200: PatchTimesheetProposalEntrySchema,
      404: TimesheetProposalNotFoundErrorSchema,
      500: ErrorGenericSchema,
    },
  },

  // ================================================
  // AI GENERATION
  // ================================================

  /**
   * AI webhook for timesheet generation completion
   */
  aiTimesheetGeneratedWebhook: {
    method: 'POST',
    path: `/1.0/ai/webhooks/timesheet_generated`,
    description:
      'Webhook endpoint called by the AI service when full timesheet generation is complete. Handles both successful and failed generations.',
    summary: 'AI timesheet generation webhook',
    body: AITimesheetGeneratedWebhookPayloadSchema,
    responses: {
      200: z.object({
        message: z.string(),
        proposal_id: GenericUUID.optional(),
      }),
      400: TimesheetProposalValidationErrorSchema,
      500: ErrorGenericSchema,
    },
  },

  /**
   * AI webhook for note regeneration completion
   */
  aiNotesRegeneratedWebhook: {
    method: 'POST',
    path: `/1.0/ai/webhooks/timesheet_notes_regenerated`,
    description:
      'Webhook endpoint called by the AI service when entry note regeneration is complete. Handles both successful and failed regenerations.',
    summary: 'AI note regeneration webhook',
    body: AINotesRegeneratedWebhookPayloadSchema,
    responses: {
      200: z.object({
        message: z.string(),
        proposal_id: GenericUUID.optional(),
        entry_ids: z.array(GenericUUID).optional(),
      }),
      400: TimesheetProposalValidationErrorSchema,
      500: ErrorGenericSchema,
    },
  },

  /**
   * AI webhook announcing that automatic AutoSheet generation was paused
   */
  aiAutoSendPausedWebhook: {
    method: 'POST',
    path: `/1.0/ai/webhooks/autosheet_paused`,
    description:
      'Webhook endpoint called by the AI service when it pauses automatic AutoSheet generation for a user after a stretch of inactivity. Dispatches an in-app + email alert telling the user how to resume automatic timesheets.',
    summary: 'AutoSheet auto-send paused webhook',
    body: AIAutoSendPausedWebhookPayloadSchema,
    responses: {
      200: z.object({
        message: z.string(),
      }),
      400: TimesheetProposalValidationErrorSchema,
      500: ErrorGenericSchema,
    },
  },

  /**
   * Trigger AI generation of a timesheet proposal
   */
  triggerTimesheetProposalGeneration: {
    method: 'POST',
    path: `/1.0/timesheet-proposals/generate`,
    description:
      'Trigger AI generation of a timesheet proposal for a specific date range and user. This initiates background processing that will analyze memories and generate time entry suggestions.',
    summary: 'Generate AI timesheet proposal',
    body: TriggerTimesheetProposalGenerationSchema,
    responses: {
      202: TriggerTimesheetProposalGenerationResponseSchema,
      400: TimesheetProposalValidationErrorSchema,
      409: z.object({
        message: z.string().openapi({
          example:
            'A proposal already exists for this date range. Use force_regenerate=true to override.',
        }),
        existing_proposal_id: GenericUUID.optional(),
      }),
      500: ErrorGenericSchema,
    },
  },

  /**
   * Regenerate notes for specific entries using AI
   */
  regenerateEntryNotes: {
    method: 'POST',
    path: `/1.0/timesheet-proposals/regenerate-notes`,
    description:
      'Trigger AI regeneration of notes for specific proposal entries. This allows refining the AI-generated descriptions based on updated memories or user feedback.',
    summary: 'Regenerate entry notes with AI',
    body: RegenerateTimesheetProposalNotesSchema,
    responses: {
      202: RegenerateTimesheetProposalNotesResponseSchema,
      400: TimesheetProposalValidationErrorSchema,
      404: TimesheetProposalNotFoundErrorSchema,
      500: ErrorGenericSchema,
    },
  },

  // ================================================
  // BULK OPERATIONS
  // ================================================

  /**
   * Bulk update multiple proposals
   */
  bulkUpdateTimesheetProposals: {
    method: 'PATCH',
    path: `/1.0/timesheet-proposals/bulk`,
    description:
      'Perform bulk operations on multiple timesheet proposals, such as accepting or rejecting multiple proposals at once.',
    summary: 'Bulk update proposals',
    body: z.object({
      proposal_ids: z
        .array(GenericUUID)
        .min(1)
        .openapi({
          description: 'Array of proposal IDs to update',
          example: ['123e4567-e89b-12d3-a456-426614174000', '987e6543-e21b-98d7-a654-624136174987'],
        }),
      action: z.enum(['accept', 'reject', 'delete']).openapi({
        description: 'Action to perform on all specified proposals',
        example: 'accept',
      }),
      log_entry: z
        .object({
          action: z.string(),
          details: z.object({}).passthrough().optional(),
        })
        .optional()
        .openapi({
          description: 'Optional log entry to record with the bulk action',
        }),
    }),
    responses: {
      200: z.object({
        updated_count: z.number().int().min(0),
        failed_ids: z.array(GenericUUID),
        message: z.string(),
      }),
      400: TimesheetProposalValidationErrorSchema,
      500: ErrorGenericSchema,
    },
  },

  // ================================================
  // ANALYTICS AND REPORTING
  // ================================================

  /**
   * Get proposal statistics
   */
  getTimesheetProposalStats: {
    method: 'GET',
    path: `/1.0/timesheet-proposals/stats`,
    description:
      'Get statistical information about timesheet proposals for the authenticated user, including acceptance rates, generation frequency, and time savings.',
    summary: 'Get proposal statistics',
    query: z.object({
      date_from: z.string().date().optional().openapi({
        description: 'Start date for statistics calculation',
        example: '2024-01-01',
      }),
      date_to: z.string().date().optional().openapi({
        description: 'End date for statistics calculation',
        example: '2024-12-31',
      }),
      connection_id: URLParamsSchema.connectionId.optional(),
    }),
    responses: {
      200: z.object({
        total_proposals: z.number().int().min(0),
        accepted_proposals: z.number().int().min(0),
        rejected_proposals: z.number().int().min(0),
        pending_proposals: z.number().int().min(0),
        acceptance_rate: z.number().min(0).max(1),
        total_time_saved_minutes: z.number().int().min(0),
        average_generation_time_seconds: z.number().min(0),
        most_active_connection: z
          .object({
            connection_id: GenericUUID,
            proposal_count: z.number().int().min(0),
          })
          .nullable(),
      }),
      500: ErrorGenericSchema,
    },
  },
});
